import React, { useCallback, useRef } from 'react';
import Link from 'next/link.js';
import { keyframes, styled } from '@components-shared/src/stitches';
import { useFadedText } from '../../hooks/useFadedText';

interface MarqueeProps {
  children: React.ReactNode;
}

export const Marquee = ({ children }: MarqueeProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isOverflowing = useFadedText(containerRef, contentRef);

  const getTextAnimation = useCallback(() => {
    // Keep scroll speed equal, regardless of content width
    const animationDurationSeconds =
      ((contentRef.current?.scrollWidth || 0) / (containerRef.current?.clientWidth || 0)) * 10;
    return {
      animation: `${animationDurationSeconds}s linear 0s infinite normal none ${marqueeKeyFrames}`,
    };
  }, []);

  return (
    // Renders a right fade on server side to avoid flash on first render, which is replaced
    // by either no fade if no overflow, or scrolling text with fade on both sides on overflow
    <MarqueeWrapper ref={containerRef} fade={isOverflowing === undefined ? 'right' : isOverflowing}>
      {isOverflowing ? (
        <>
          <MarqueeItem ref={contentRef} css={getTextAnimation()}>
            {children}
          </MarqueeItem>
          <Space />
          <MarqueeItem css={getTextAnimation()}>{children}</MarqueeItem>
          <Space />
        </>
      ) : (
        <MarqueeItem ref={contentRef}>{children}</MarqueeItem>
      )}
    </MarqueeWrapper>
  );
};

const MarqueeWrapper = styled('div', {
  height: '100%',
  display: 'inline-flex',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  width: '100%',

  variants: {
    fade: {
      true: {
        maskImage: 'linear-gradient(90deg, transparent, black 10%, black 90%, transparent)',
      },
      right: {
        maskImage: 'linear-gradient(90deg, black 0%, black 90%, transparent)',
      },
    },
  },
});

const marqueeKeyFrames = keyframes({
  '0%': {
    transform: 'translateX(0) ',
  },
  '100%': {
    transform: 'translateX(calc(-1 * (100% + 20px)))',
  },
});

const MarqueeItem = styled('div', {
  display: 'inline-block',
});

const Space = styled('div', {
  width: 20,
  display: 'inline-block',
  flexShrink: 0,
});

// Can be used to avoid links overflowing their parent
export const FullWidthLink = styled(Link, {
  width: '100%',
});
